/**
 * react-query bindings for the API.
 *
 * Polling periods live in `src/config.ts`; every live query stops polling when
 * the tab is hidden (`useDocumentVisible`). Mutations invalidate exactly the
 * queries whose truth they changed — the UI never patches a cached object with
 * an optimistic guess, because the cluster is the source of truth.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  InfiniteData,
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
} from "@tanstack/react-query";

import {
  ADMIN_POLL_MS,
  AUDIT_LIMIT,
  CPU_RUN_LOG_TAIL,
  CPU_RUN_POLL_MS,
  CPU_TASK_PAGE_SIZE,
  HOME_POLL_MS,
  JOB_LOG_TAIL,
  JOB_POLL_MS,
  RELAY_STALE_MS,
  WORKSPACE_ACTIVE_POLL_MS,
  WORKSPACE_IDLE_POLL_MS,
  WORKSPACE_LOG_TAIL,
} from "../config";
import { pollInterval, useDocumentVisible } from "../lib/visibility";
import { useApi, useSessionLost } from "./ApiProvider";
import { ApiError } from "./client";
import { CPU_WORKSPACES_KEY, queryKeys } from "./keys";
import type {
  AdminUser,
  AdminUserList,
  AuditList,
  AuditQuery,
  AuthConfig,
  Capacity,
  ConsoleKeyCreate,
  ConsoleKeyList,
  CpuRun,
  CpuRunCreate,
  CpuRunList,
  CpuRunListQuery,
  CpuRunTaskList,
  CpuTaskState,
  CpuWorkspace,
  CpuWorkspaceCreate,
  CreatedConsoleKey,
  DisableRequest,
  Entitlement,
  EnvironmentCreate,
  EnvironmentList,
  EnvironmentOption,
  EnvironmentUpdate,
  ExtendRequest,
  Job,
  JobCreate,
  JobList,
  JobListQuery,
  Me,
  MeUpdate,
  NodeRequest,
  NodeRequestResult,
  PauseRequest,
  PauseState,
  Relay,
  ResizeRequest,
  ShellUpdate,
  ShortenRequest,
  MyUsage,
  UsageReport,
  Workspace,
  WorkspaceCreate,
  WorkspaceList,
} from "./types";
import {
  PENDING_WORKSPACE_STATES,
  TERMINAL_CPU_RUN_STATES,
  TERMINAL_JOB_STATES,
  TERMINAL_WORKSPACE_STATES,
} from "./types";

export { queryKeys };

/** How the server signs people in; asked once, it does not change at runtime. */
export function useAuthConfig(): UseQueryResult<AuthConfig, Error> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.authConfig,
    queryFn: ({ signal }) => api.getAuthConfig(signal),
    staleTime: Infinity,
  });
}

/**
 * Measured cluster capacity, refreshed while the tab is visible.
 *
 * Args:
 *     enabled: Set to false where capacity is not on screen; the query key is
 *         shared, so an enabled caller elsewhere still serves this one from
 *         cache instead of asking the server twice.
 *
 * Returns:
 *     The capacity query.
 */
export function useCapacity(enabled = true): UseQueryResult<Capacity, Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.capacity,
    queryFn: ({ signal }) => api.getCapacity(signal),
    enabled,
    refetchInterval: pollInterval(HOME_POLL_MS, visible),
  });
}

/**
 * Every live workspace you hold — all of them, not just the newest.
 *
 * A researcher may hold several workspaces at once (`docs/API.md`); a home
 * screen that shows only the latest makes the others invisible, which is how
 * an 8-hour workspace gets forgotten and burns its GPUs.
 */
export function useMyWorkspaces(): UseQueryResult<Workspace[], Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.myWorkspaces,
    queryFn: async ({ signal }) => {
      const list = await api.listWorkspaces({ mine: true, limit: 50 }, signal);
      return list.items.filter(
        (item) => !TERMINAL_WORKSPACE_STATES.includes(item.state),
      );
    },
    refetchInterval: pollInterval(HOME_POLL_MS, visible),
  });
}

/** One workspace, polled faster while it is not usable yet. */
export function useWorkspace(id: string): UseQueryResult<Workspace, Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.workspace(id),
    queryFn: ({ signal }) => api.getWorkspace(id, signal),
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      const period =
        state && PENDING_WORKSPACE_STATES.includes(state)
          ? WORKSPACE_ACTIVE_POLL_MS
          : WORKSPACE_IDLE_POLL_MS;
      return pollInterval(period, visible);
    },
  });
}

/** The startup log of a workspace; only fetched when asked for. */
export function useWorkspaceLogs(
  id: string,
  enabled: boolean,
): UseQueryResult<string, Error> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.workspaceLogs(id),
    queryFn: ({ signal }) => api.getWorkspaceLogs(id, WORKSPACE_LOG_TAIL, signal),
    enabled,
  });
}

/** Every environment the caller can pick: built-in plus saved, one list. */
export function useEnvironments(): UseQueryResult<EnvironmentList, Error> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.environments,
    queryFn: ({ signal }) => api.listEnvironments(signal),
    staleTime: 60_000,
  });
}

/** Save a new environment. */
export function useCreateEnvironment(): UseMutationResult<
  EnvironmentOption,
  Error,
  EnvironmentCreate
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: EnvironmentCreate) => api.createEnvironment(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.environments });
    },
  });
}

/** Change a saved environment you own. */
export function useUpdateEnvironment(): UseMutationResult<
  EnvironmentOption,
  Error,
  { id: string } & EnvironmentUpdate
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & EnvironmentUpdate) =>
      api.updateEnvironment(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.environments });
    },
  });
}

/** Delete a saved environment you own. */
export function useDeleteEnvironment(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteEnvironment(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.environments });
    },
  });
}

/**
 * Your account: identity, approval state and SSH key.
 *
 * It waits for the published configuration and stops being asked the moment
 * the server refuses the session — there is nobody left to ask about.
 *
 * The wait is not cosmetic: in the server's development mode the identity is a
 * header built from that configuration, and a `/me` sent before it arrived
 * would be anonymous, answered `401`, and would drop a laptop straight to the
 * sign-in screen it cannot use. Every other query is only mounted once this
 * one has answered, so none of them can race it.
 *
 * Returns:
 *     The account query.
 */
export function useMe(): UseQueryResult<Me, Error> {
  const api = useApi();
  const sessionLost = useSessionLost();
  const config = useAuthConfig();
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: ({ signal }) => api.getMe(signal),
    enabled: !sessionLost && config.isSuccess,
    staleTime: 30_000,
  });
}

/**
 * *Training elsewhere*: the lines to paste, and whether a key backs them.
 *
 * Asked for by every approved researcher, unconditionally. There is no
 * entitlement to consult first: reaching that machine comes with approval, the
 * screen belongs to everybody, and what an admin has recorded only decides
 * whether the answer carries its `jean_zay` half (`docs/AUTH.md` §2).
 *
 * Nothing here is polled: the address comes from the server's configuration and
 * the key from the researcher. What does move — the machine installing that key
 * — happens at its own pace and the console cannot see it (`docs/AUTH.md` §4b),
 * so a refresh loop would only redraw the same sentence.
 *
 * Returns:
 *     The *Training elsewhere* query.
 */
export function useRelay(): UseQueryResult<Relay, Error> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.relay,
    queryFn: ({ signal }) => api.getRelay(signal),
    staleTime: RELAY_STALE_MS,
  });
}

/** Jobs, newest first as served by the API. */
export function useJobs(query: JobListQuery): UseQueryResult<JobList, Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.jobs(query),
    queryFn: ({ signal }) => api.listJobs(query, signal),
    refetchInterval: pollInterval(JOB_POLL_MS, visible),
  });
}

/** One job, polled until it stops moving. */
export function useJob(id: string): UseQueryResult<Job, Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.job(id),
    queryFn: ({ signal }) => api.getJob(id, signal),
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      if (state && TERMINAL_JOB_STATES.includes(state)) {
        return false;
      }
      return pollInterval(JOB_POLL_MS, visible);
    },
  });
}

/** The log of a job. */
export function useJobLogs(
  id: string,
  enabled: boolean,
): UseQueryResult<string, Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.jobLogs(id),
    queryFn: ({ signal }) => api.getJobLogs(id, JOB_LOG_TAIL, signal),
    enabled,
    refetchInterval: pollInterval(JOB_POLL_MS, visible),
  });
}

// ----------------------------------------------------------------- CPU runs

/**
 * The CPU runs you submitted, newest first as served by the API.
 *
 * Args:
 *     query: Filters passed straight to the API.
 *     enabled: False where the deployment has no CPU fleet at all — the paths
 *         do not exist there, and asking would only collect 404s.
 *
 * Returns:
 *     The runs query.
 */
export function useCpuRuns(
  query: CpuRunListQuery,
  enabled = true,
): UseQueryResult<CpuRunList, Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.cpuRuns(query),
    queryFn: ({ signal }) => api.listCpuRuns(query, signal),
    enabled,
    refetchInterval: pollInterval(CPU_RUN_POLL_MS, visible),
  });
}

/**
 * One run, polled until it stops moving, asking only for what is new.
 *
 * The first read is the whole trace; every read after it passes
 * `events_after` and appends what comes back. A run of a thousand tasks writes
 * a `progress` line every time its count moves, so re-reading the whole trace
 * every ten seconds would be paying for it instead of reading it.
 *
 * The append filters on the index rather than trusting the narrowing, so a
 * console that has not learned `events_after` yet — it answers with the whole
 * trace — still lands on exactly one copy of every line.
 */
export function useCpuRun(id: string): UseQueryResult<CpuRun, Error> {
  const api = useApi();
  const queryClient = useQueryClient();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.cpuRun(id),
    queryFn: async ({ signal }) => {
      const held = queryClient.getQueryData<CpuRun>(queryKeys.cpuRun(id));
      const last = held?.events.at(-1)?.index;
      if (held === undefined || last === undefined) {
        return api.getCpuRun(id, {}, signal);
      }
      const fresh = await api.getCpuRun(id, { events_after: last }, signal);
      return {
        ...fresh,
        events: [
          ...held.events,
          ...fresh.events.filter((event) => event.index > last),
        ],
      };
    },
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      if (state && TERMINAL_CPU_RUN_STATES.includes(state)) {
        return false;
      }
      return pollInterval(CPU_RUN_POLL_MS, visible);
    },
  });
}

/**
 * The tasks of a run, one keyset page at a time.
 *
 * Paging is on the index and never on an offset, so a page does not shift
 * under a run whose rows are changing — and a page is a snapshot either way:
 * a task that was running when the first page was built may have succeeded
 * before the second arrives. What has to be exact is the counter on the run.
 *
 * Args:
 *     id: The run.
 *     state: The single task state to narrow to, or undefined for all of them.
 *     live: False once the run has finished — its rows cannot move again, and
 *         polling a finished run is asking a question with a known answer.
 *
 * Returns:
 *     The paged tasks query.
 */
export function useCpuRunTasks(
  id: string,
  state: CpuTaskState | undefined,
  live: boolean,
): UseInfiniteQueryResult<InfiniteData<CpuRunTaskList>, Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useInfiniteQuery({
    queryKey: queryKeys.cpuRunTasks(id, state ?? "all"),
    queryFn: ({ pageParam, signal }) =>
      api.listCpuRunTasks(
        id,
        {
          ...(state === undefined ? {} : { state }),
          ...(pageParam === null ? {} : { after: pageParam }),
          limit: CPU_TASK_PAGE_SIZE,
        },
        signal,
      ),
    initialPageParam: null as number | null,
    getNextPageParam: (last: CpuRunTaskList) => last.next_after,
    refetchInterval: live ? pollInterval(CPU_RUN_POLL_MS, visible) : false,
  });
}

/** The log of one task of a run. */
export function useCpuRunLogs(
  id: string,
  task: number,
  enabled: boolean,
): UseQueryResult<string, Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.cpuRunLogs(id, task),
    queryFn: ({ signal }) =>
      api.getCpuRunLogs(id, CPU_RUN_LOG_TAIL, task, signal),
    enabled,
    refetchInterval: pollInterval(CPU_RUN_POLL_MS, visible),
  });
}

/** Submit a run. Capacity moves with it, so it is re-read too. */
export function useCreateCpuRun(): UseMutationResult<
  CpuRun,
  Error,
  CpuRunCreate
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CpuRunCreate) => api.createCpuRun(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["cpu-runs"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.capacity });
    },
  });
}

/** Stop a run: every task running, and every task not yet started. */
export function useCancelCpuRun(
  id: string,
): UseMutationResult<CpuRun | null, Error, void> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.cancelCpuRun(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["cpu-runs"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.capacity });
    },
  });
}

/** Push a run's deadline — the only thing that bounds a running run. */
export function useExtendCpuRun(
  id: string,
): UseMutationResult<CpuRun, Error, ExtendRequest> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ExtendRequest) => api.extendCpuRun(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cpuRun(id) });
      void queryClient.invalidateQueries({ queryKey: ["cpu-runs"] });
    },
  });
}

// ----------------------------------------------------------- CPU workspaces

/**
 * Every live CPU workspace you hold, the shape `useMyWorkspaces` has.
 *
 * Args:
 *     enabled: False where the deployment has no CPU fleet at all — the paths
 *         do not exist there, and asking would only collect 404s.
 *
 * Returns:
 *     The query, terminal workspaces already filtered out.
 */
export function useMyCpuWorkspaces(
  enabled = true,
): UseQueryResult<CpuWorkspace[], Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.myCpuWorkspaces,
    queryFn: async ({ signal }) => {
      const list = await api.listCpuWorkspaces({ mine: true, limit: 50 }, signal);
      return list.items.filter(
        (item) => !TERMINAL_WORKSPACE_STATES.includes(item.state),
      );
    },
    enabled,
    refetchInterval: pollInterval(HOME_POLL_MS, visible),
  });
}

/** One CPU workspace, polled faster while it is not usable yet. */
export function useCpuWorkspace(
  id: string,
): UseQueryResult<CpuWorkspace, Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.cpuWorkspace(id),
    queryFn: ({ signal }) => api.getCpuWorkspace(id, signal),
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      const period =
        state && PENDING_WORKSPACE_STATES.includes(state)
          ? WORKSPACE_ACTIVE_POLL_MS
          : WORKSPACE_IDLE_POLL_MS;
      return pollInterval(period, visible);
    },
  });
}

/** The startup log of a CPU workspace; only fetched when asked for. */
export function useCpuWorkspaceLogs(
  id: string,
  enabled: boolean,
): UseQueryResult<string, Error> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.cpuWorkspaceLogs(id),
    queryFn: ({ signal }) =>
      api.getCpuWorkspaceLogs(id, WORKSPACE_LOG_TAIL, signal),
    enabled,
  });
}

/** Open a CPU workspace. Cores move with it, so capacity is re-read too. */
export function useCreateCpuWorkspace(): UseMutationResult<
  CpuWorkspace,
  Error,
  CpuWorkspaceCreate
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CpuWorkspaceCreate) => api.createCpuWorkspace(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CPU_WORKSPACES_KEY });
      void queryClient.invalidateQueries({ queryKey: queryKeys.capacity });
    },
  });
}

/**
 * Push a CPU workspace's deadline.
 *
 * The only clock this kind has: nothing reclaims it for being idle, so its
 * deadline is what ends it.
 */
export function useExtendCpuWorkspace(
  id: string,
): UseMutationResult<CpuWorkspace, Error, ExtendRequest> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ExtendRequest) => api.extendCpuWorkspace(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cpuWorkspace(id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.myCpuWorkspaces,
      });
    },
  });
}

/** Stop a CPU workspace and give its cores back. */
export function useStopCpuWorkspace(
  id: string,
): UseMutationResult<CpuWorkspace | null, Error, void> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.stopCpuWorkspace(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CPU_WORKSPACES_KEY });
      void queryClient.invalidateQueries({ queryKey: queryKeys.capacity });
    },
  });
}

/** Ask Scaleway for one more machine. */
export function useStartNodes(): UseMutationResult<
  NodeRequestResult,
  Error,
  NodeRequest
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: NodeRequest) => api.startNodes(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.capacity });
    },
  });
}

/** Create the workspace. */
export function useCreateWorkspace(): UseMutationResult<
  Workspace,
  Error,
  WorkspaceCreate
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: WorkspaceCreate) => api.createWorkspace(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.myWorkspaces });
      void queryClient.invalidateQueries({ queryKey: queryKeys.capacity });
    },
  });
}

/** Push the deadline of a workspace out — as often as the work needs. */
export function useExtendWorkspace(
  id: string,
): UseMutationResult<Workspace, Error, ExtendRequest> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ExtendRequest) => api.extendWorkspace(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workspace(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.myWorkspaces });
    },
  });
}

/**
 * Bring the deadline in, giving the GPUs back early.
 *
 * The workspace keeps running, so nothing about the connection changes — but
 * the fleet is about to get those GPUs back at a known time, and the capacity
 * screen shows deadlines, so it is re-read too.
 */
export function useShortenWorkspace(
  id: string,
): UseMutationResult<Workspace, Error, ShortenRequest> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ShortenRequest) => api.shortenWorkspace(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workspace(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.myWorkspaces });
      void queryClient.invalidateQueries({ queryKey: queryKeys.capacity });
    },
  });
}

/**
 * Change how many GPUs a workspace holds — more, or back to fewer.
 *
 * The environment restarts on the new count; the home folder is untouched.
 * Capacity moves with it, so both caches are re-read.
 */
export function useResizeWorkspace(
  id: string,
): UseMutationResult<Workspace, Error, ResizeRequest> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ResizeRequest) => api.resizeWorkspace(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workspace(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.myWorkspaces });
      void queryClient.invalidateQueries({ queryKey: queryKeys.capacity });
    },
  });
}

/** Stop a workspace and give its GPUs back. */
export function useStopWorkspace(
  id: string,
): UseMutationResult<Workspace | null, Error, void> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.stopWorkspace(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workspace(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.myWorkspaces });
      void queryClient.invalidateQueries({ queryKey: queryKeys.capacity });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminWorkspaces });
    },
  });
}

/**
 * Replace the whole list of your SSH public keys. Allowed while pending.
 *
 * Adding a key is sending the ones on file plus the new one; removing one is
 * sending the others. Nobody ever retypes a key they already registered.
 */
export function useSaveSshKeys(): UseMutationResult<Me, Error, MeUpdate> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: MeUpdate) => api.updateMe(payload),
    onSuccess: (me) => {
      queryClient.setQueryData(queryKeys.me, me);
      // Training elsewhere derives key_on_file/ready from the same list, so a
      // key saved on the account screen must reach that screen too — without
      // this, /relay keeps saying "no key for this machine" about a key that
      // is already on file.
      void queryClient.invalidateQueries({ queryKey: queryKeys.relay });
    },
  });
}

/** Choose the shell your next workspace hands you. */
export function useSaveShell(): UseMutationResult<Me, Error, ShellUpdate> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ShellUpdate) => api.updateShell(payload),
    onSuccess: (me) => {
      queryClient.setQueryData(queryKeys.me, me);
    },
  });
}

// ---------------------------------------------------------- console keys

/**
 * The console keys that can reach this account right now.
 *
 * Live keys only — the server does not list revoked or expired ones, because
 * this answers *what can reach my account right now*. It is **not polled**:
 * `last_used_at` is written at a resolution of one minute, so a ticking list
 * would suggest a liveness the value does not have.
 */
export function useConsoleKeys(): UseQueryResult<ConsoleKeyList, Error> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.consoleKeys,
    queryFn: ({ signal }) => api.listConsoleKeys(signal),
  });
}

/**
 * Mint a console key for the command line.
 *
 * The secret is in the answer and nowhere else: this application never writes
 * it to storage, never puts it in a URL and never sends it anywhere. It lives
 * in the mutation's result while the screen shows it once, and is dropped the
 * moment that panel is dismissed or left.
 */
export function useCreateConsoleKey(): UseMutationResult<
  CreatedConsoleKey,
  Error,
  ConsoleKeyCreate
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ConsoleKeyCreate) => api.createConsoleKey(payload),
    // Not the five default minutes: the answer holds a secret, so it is
    // dropped the instant nothing is showing it — leaving the page, or saying
    // it has been copied, is enough to forget it.
    gcTime: 0,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.consoleKeys });
    },
  });
}

/** Revoke one of your own keys; it is dead at its next request. */
export function useRevokeConsoleKey(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.revokeConsoleKey(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.consoleKeys });
    },
  });
}

/** Submit a batch job. */
export function useCreateJob(): UseMutationResult<Job, Error, JobCreate> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: JobCreate) => api.createJob(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

/** Cancel a job. */
export function useCancelJob(
  id: string,
): UseMutationResult<Job | null, Error, void> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.cancelJob(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["jobs"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.capacity });
    },
  });
}

/** End the session on the server; the caller then reloads the application. */
export function useSignOut(): UseMutationResult<void, Error, void> {
  const api = useApi();
  return useMutation({ mutationFn: () => api.logout() });
}

// -------------------------------------------------------------------- admin

/** Every account the console knows, newest sign-ins included. */
export function useAccounts(): UseQueryResult<AdminUserList, Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.adminUsers,
    queryFn: ({ signal }) => api.listAccounts(signal),
    refetchInterval: pollInterval(ADMIN_POLL_MS, visible),
  });
}

/**
 * What every person consumed over the last `days` days.
 *
 * Read once per period chosen, not polled: it is a report somebody reads,
 * not a state somebody watches.
 */
export function useUsage(days: number): UseQueryResult<UsageReport, Error> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.adminUsage(days),
    queryFn: ({ signal }) => api.getUsage(days, signal),
  });
}

/**
 * Your own consumption over the last `days` days, and what it cost.
 *
 * Read once per period, not polled: it is something a person reads about
 * themselves, not a state anybody watches.
 */
export function useMyUsage(days: number): UseQueryResult<MyUsage, Error> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.myUsage(days),
    queryFn: ({ signal }) => api.getMyUsage(days, signal),
    staleTime: 60_000,
  });
}

/** Every live workspace, whoever owns it. */
export function useAllWorkspaces(): UseQueryResult<WorkspaceList, Error> {
  const api = useApi();
  const visible = useDocumentVisible();
  return useQuery({
    queryKey: queryKeys.adminWorkspaces,
    queryFn: ({ signal }) => api.listAllWorkspaces(signal),
    refetchInterval: pollInterval(ADMIN_POLL_MS, visible),
  });
}

/**
 * One page of the audit log, newest first.
 *
 * The filters are part of the key, so each page and each search is cached on
 * its own and stepping back to a page already read is instant.
 */
export function useAuditLog(
  query: AuditQuery = { limit: AUDIT_LIMIT, offset: 0 },
): UseQueryResult<AuditList, Error> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.adminAudit(query),
    queryFn: ({ signal }) => api.getAuditLog(query, signal),
    placeholderData: (previous) => previous,
  });
}

/** Grant researcher access to an account waiting for it. */
export function useApproveAccount(): UseMutationResult<
  AdminUser | null,
  Error,
  string
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (username: string) => api.approveAccount(username),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });
      void queryClient.invalidateQueries({ queryKey: ["admin", "audit"] });
    },
  });
}

/** Turn an account off; their live workspace is stopped with it. */
export function useDisableAccount(): UseMutationResult<
  AdminUser | null,
  Error,
  { username: string } & DisableRequest
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ username, reason }: { username: string } & DisableRequest) =>
      api.disableAccount(username, { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminWorkspaces });
      void queryClient.invalidateQueries({ queryKey: ["admin", "audit"] });
    },
  });
}

/**
 * Queries a record changes: who is marked, the audit of it, and the one screen
 * it decides.
 *
 * `/relay` carries its Jean Zay half only for a marked account, and an admin
 * marks themselves like anybody else — so `/me` and the section are both
 * re-read. Nothing about who may log into that machine changes here.
 */
function useEntitlementMutation(
  call: (username: string) => Promise<Entitlement>,
): UseMutationResult<Entitlement, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: call,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });
      void queryClient.invalidateQueries({ queryKey: ["admin", "audit"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
      void queryClient.invalidateQueries({ queryKey: queryKeys.relay });
    },
  });
}

/**
 * Record that the team has declared an account to IDRIS.
 *
 * Marking twice is not an error, and it hands nobody a shell they did not
 * already have: that machine's key list is every approved account with a key.
 */
export function useGrantJeanZay(): UseMutationResult<Entitlement, Error, string> {
  const api = useApi();
  return useEntitlementMutation((username) => api.grantJeanZay(username));
}

/** Take the record back; the row is stamped, so who was declared stays readable. */
export function useRevokeJeanZay(): UseMutationResult<Entitlement, Error, string> {
  const api = useApi();
  return useEntitlementMutation((username) => api.revokeJeanZay(username));
}

/** The emergency stop: refuse new workspaces and new machines, or allow them again. */
export function useSetPaused(): UseMutationResult<
  PauseState | null,
  Error,
  PauseRequest
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PauseRequest) => api.setPaused(payload),
    onSuccess: () => {
      // `console_paused` is published on `/me`; that is what every screen
      // renders, so it is what must be re-read.
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
      void queryClient.invalidateQueries({ queryKey: queryKeys.capacity });
      void queryClient.invalidateQueries({ queryKey: ["admin", "audit"] });
    },
  });
}

/**
 * Human-readable message for anything thrown by a query or a mutation.
 *
 * Args:
 *     error: The thrown value.
 *
 * Returns:
 *     The server's message when there is one, else the error text.
 */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Something went wrong.";
}
